import React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import useFetchObjects from "../../api/useFetchObjects";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import { Button, Divider, Grid, Menu, Pagination, Select } from "@mui/material";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import LinearProgress from "@mui/material/LinearProgress";
import RefreshIcon from "@mui/icons-material/Refresh";
import { exportPurchaseReturnsToPDF } from "../../utils/exportPurchaseReturn";

const firstCurrencyValue = (obj) => {
  if (obj && typeof obj === "object") {
    const firstEntry = Object.values(obj)[0];
    if (firstEntry && typeof firstEntry === "object")
      return firstEntry.source ?? "-";
    return firstEntry ?? "-";
  }
  return obj ?? "-";
};

function Row({ row }) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const itemCount = row.return_items?.length ?? 0;

  const currency = row.purchase_details
    ? Object.keys(row.purchase_details.total_amount || {})[0] || ""
    : "";

  return (
    <>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>

        <TableCell>{row.purchase_details?.id ? `Purchase #${row.purchase_details.id}` : "-"}</TableCell>
        <TableCell>{row.return_date?.slice(0, 10) ?? "-"}</TableCell>
        <TableCell>{row.reason ?? "-"}</TableCell>
        <TableCell>
          {row.purchase_details?.total_amount ? 
            `${firstCurrencyValue(row.purchase_details.total_amount)} ${currency}` : 
            (row.purchase_details?.purchase_items?.reduce((sum, item) => sum + (item.buy_price * item.quantity), 0) || 0).toFixed(2)
          }
        </TableCell>
        <TableCell>
          {row.total_refound_amount || 
            row.return_items?.reduce((sum, item) => sum + parseFloat(item.refund_amount || 0), 0).toFixed(2) || "0.00"
          } {currency}
        </TableCell>
        <TableCell>{itemCount}</TableCell>
      </TableRow>

      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ m: 1 }}>
              <Typography variant="h6" gutterBottom component="div">
                {t("ReportIndex.returnPurchase_collapse_title")}
              </Typography>

              <Table size="small" aria-label="return-items">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {t("ReportIndex.returnPurchase_collapse_product")}
                    </TableCell>
                    <TableCell>
                      {t("ReportIndex.returnPurchase_collapse_qty")}
                    </TableCell>
                    <TableCell>
                      {t("ReportIndex.returnPurchase_collapse_price")}
                    </TableCell>
                    <TableCell>
                      {t("ReportIndex.returnPurchase_collapse_refundAmount")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {row.return_items?.map((item) => {
                    const buyPrice = firstCurrencyValue(
                      item.purchase_item_details?.buy_price
                    );
                    return (
                      <TableRow key={item.id}>
                        <TableCell component="th" scope="row">
                          {item.purchase_item_details?.product_details?.name}
                        </TableCell>
                        <TableCell>{item.return_quantity}</TableCell>
                        <TableCell>{buyPrice}</TableCell>
                        <TableCell>{item.refund_amount}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

Row.propTypes = {
  row: PropTypes.shape({
    purchase_details: PropTypes.object,
    return_date: PropTypes.string,
    reason: PropTypes.string,
    total_refound_amount: PropTypes.string,
    return_items: PropTypes.array,
  }).isRequired,
};

export default function PurchaseReturnTable({ selectedBranch = "" }) {
  const auth = useAuth();
  const token = auth?.user?.token;
  const { t } = useTranslation();

  const [startDate, setStartDate] = React.useState(null);
  const [endDate, setEndDate] = React.useState(null);
  const [selectedPurchaseId, setSelectedPurchaseId] = React.useState("");
  const [selectedSupplier, setSelectedSupplier] = React.useState("");
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [page, setPage] = React.useState(1);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const queryString = React.useMemo(() => {
    const params = [];
    if (startDate) params.push(`start_date=${startDate.format("YYYY-MM-DD")}`);
    if (endDate) params.push(`end_date=${endDate.format("YYYY-MM-DD")}`);
    if (selectedBranch) params.push(`shop=${selectedBranch}`);
    return params.join("&");
  }, [startDate, endDate, selectedBranch]);

  const {
    data: returnsData,
    isLoading,
    isError,
  } = useFetchObjects(
    ["purchase-returns", queryString, selectedPurchaseId, selectedSupplier],
    `shop/purchase-returns/?${queryString}`,
    token
  );

  // Fetch suppliers for filter dropdown
  const { data: suppliersData } = useFetchObjects(
    ["suppliers"],
    "shop/suppliers",
    token
  );

  const suppliers = suppliersData?.results || [];

  const returns = returnsData?.results || returnsData || [];
  const totalCount = returnsData?.count || returns.length;

  // Debug log
  console.log('Purchase Returns Data:', returnsData);
  console.log('Returns Array:', returns);
  console.log('Total Count:', totalCount);

  // Filter by purchase ID and supplier locally
  const filteredReturns = React.useMemo(() => {
    let filtered = returns;
    if (selectedPurchaseId) {
      filtered = filtered.filter(ret => 
        ret.purchase_details?.id?.toString().includes(selectedPurchaseId)
      );
    }
    if (selectedSupplier) {
      filtered = filtered.filter(ret => 
        ret.purchase_details?.supplier_details?.name?.toLowerCase().includes(selectedSupplier.toLowerCase())
      );
    }
    return filtered;
  }, [returns, selectedPurchaseId, selectedSupplier]);

  const finalCount = filteredReturns.length;
  const displayReturns = React.useMemo(() => {
    if (rowsPerPage === -1) {
      return filteredReturns;
    }
    const startIndex = (page - 1) * rowsPerPage;
    return filteredReturns.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredReturns, page, rowsPerPage]);

  // Calculate summary totals
  const summaryTotals = React.useMemo(() => {
    return displayReturns.reduce((acc, ret) => {
      const purchaseTotal = ret.purchase_details?.total_amount ? 
        parseFloat(firstCurrencyValue(ret.purchase_details.total_amount)) : 
        (ret.purchase_details?.purchase_items?.reduce((sum, item) => sum + (item.buy_price * item.quantity), 0) || 0);
      
      const refundTotal = parseFloat(ret.total_refound_amount) || 
        ret.return_items?.reduce((sum, item) => sum + parseFloat(item.refund_amount || 0), 0) || 0;
      
      return {
        totalPurchaseAmount: acc.totalPurchaseAmount + purchaseTotal,
        totalRefundAmount: acc.totalRefundAmount + refundTotal
      };
    }, { totalPurchaseAmount: 0, totalRefundAmount: 0 });
  }, [displayReturns]);

  const open = Boolean(anchorEl);
  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleExportClick = (type) => {
    const today = new Date().toISOString().slice(0, 10);
    const base = `purchase_returns_${today}`;

    if (type === "pdf") {
      exportPurchaseReturnsToPDF(returns, `${base}.pdf`);
    }
    handleClose();
  };

  if (isLoading)
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          width: "100%",
        }}
      >
        <LinearProgress sx={{ width: "90%" }} />
      </Box>
    );

  if (isError)
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          textAlign: "center",
        }}
      >
        <Typography variant="h6" color="error">
          {t("ReportIndex.error_fetching_data")}
          <RefreshIcon sx={{ cursor: "pointer", verticalAlign: "middle" }} />
        </Typography>
      </Box>
    );

  return (
    <>
      <Box sx={{ flexGrow: 1, mb: 5, m: "15px" }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: 2,
                  alignItems: { xs: "stretch", md: "center" },
                }}
              >
                <DatePicker
                  label={t("ReportIndex.datepicker_start")}
                  value={startDate}
                  onChange={setStartDate}
                  slotProps={{ textField: { fullWidth: true } }}
                />
                <DatePicker
                  label={t("ReportIndex.datepicker_end")}
                  value={endDate}
                  onChange={setEndDate}
                  slotProps={{ textField: { fullWidth: true } }}
                />
                <FormControl fullWidth>
                  <Select
                    value={selectedPurchaseId}
                    onChange={(e) => setSelectedPurchaseId(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="">{t("ReportIndex.all_purchase_ids")}</MenuItem>
                    {returns.map((ret, index) => (
                      <MenuItem key={index} value={ret.purchase_details?.id?.toString() || ""}>
                        Purchase #{ret.purchase_details?.id || "N/A"}
                      </MenuItem>
                    )).filter((item, index, self) => 
                      index === self.findIndex(t => t.props.value === item.props.value)
                    )}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <Select
                    value={selectedSupplier}
                    onChange={(e) => setSelectedSupplier(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="">{t("ReportIndex.all_suppliers")}</MenuItem>
                    {(suppliers || []).map((supplier) => (
                      <MenuItem key={supplier.id} value={supplier.name}>
                        {supplier.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => {
                    setStartDate(null);
                    setEndDate(null);
                    setSelectedPurchaseId("");
                    setSelectedSupplier("");
                    setPage(1);
                  }}
                  sx={{
                    whiteSpace: "nowrap",
                    width: { xs: "100%", md: "auto" },
                    flexShrink: 0,
                    height: 55,
                  }}
                >
                  {t("ReportIndex.btn_clear")}
                </Button>
              </Box>
            </Grid>

            <Grid
              item
              xs={12}
              md={4}
              sx={{
                display: "flex",
                justifyContent: { xs: "flex-start", md: "flex-end" },
              }}
            >
              <FormControl
                sx={{ m: 1, minWidth: 200, width: { xs: "100%", md: "auto" } }}
              >
                <Box>
                  <Button
                    id="export-button"
                    aria-controls={open ? "export-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? "true" : undefined}
                    onClick={handleClick}
                    variant="outlined"
                    fullWidth
                    sx={{
                      whiteSpace: "nowrap",
                      width: { xs: "100%", md: "auto" },
                      flexShrink: 0,
                      height: 55,
                    }}
                  >
                    {t("ReportIndex.btn_export")}
                  </Button>
                  <Menu
                    id="export-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    MenuListProps={{
                      "aria-labelledby": "export-button",
                    }}
                  >
                    <MenuItem onClick={() => handleExportClick("excel")}>
                      {t("ReportIndex.export_excel")}
                    </MenuItem>
                    <MenuItem onClick={() => handleExportClick("pdf")}>
                      {t("ReportIndex.export_pdf")}
                    </MenuItem>
                    <MenuItem onClick={() => handleExportClick("none")}>
                      {t("ReportIndex.export_none")}
                    </MenuItem>
                  </Menu>
                </Box>
              </FormControl>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Box>

      <Divider />

      <TableContainer component={Paper}>
        <Table aria-label="purchase returns table">
          <TableHead>
            <TableRow>
              <TableCell colSpan={8} style={{ textAlign: "center" }}>
                <Typography variant="h4">
                  {t("ReportIndex.returnPurchase_tableTitle")}
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>{t("ReportIndex.returnPurchase_table_purchaseID")}</TableCell>
              <TableCell>{t("ReportIndex.returnPurchase_table_date")}</TableCell>
              <TableCell>{t("ReportIndex.returnPurchase_table_reason")}</TableCell>
              <TableCell>
                {t("ReportIndex.returnPurchase_table_purchaseTotal")}
              </TableCell>
              <TableCell>
                {t("ReportIndex.returnPurchase_table_refundTotal")}
              </TableCell>
              <TableCell>
                {t("ReportIndex.returnPurchase_table_itemCount")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayReturns.map((ret) => (
              <Row key={ret.id} row={ret} />
            ))}
            {/* Summary Row */}
            <TableRow sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
              <TableCell />
              <TableCell />
              <TableCell />
              <TableCell sx={{ fontWeight: 'bold' }}>{t('Summary')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {summaryTotals.totalPurchaseAmount.toFixed(2)}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {summaryTotals.totalRefundAmount.toFixed(2)}
              </TableCell>
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Grid container justifyContent="center" alignItems="center" padding="10px">
        <Box display="flex" alignItems="center">
          <Typography>{t("expense.rowsPerPage", "Rows Per Page")}</Typography>
          <FormControl sx={{ "& .MuiOutlinedInput-root": { "& fieldset": { border: "none" } } }}>
            <Select value={rowsPerPage} onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }}>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={30}>30</MenuItem>
              <MenuItem value={-1}>{t("reports.all")}</MenuItem>
            </Select>
          </FormControl>
        </Box>
        {rowsPerPage !== -1 && (
          <Pagination
            count={Math.ceil(finalCount / rowsPerPage)}
            page={page}
            onChange={(e, newPage) => setPage(newPage)}
            variant="outlined"
            color="primary"
          />
        )}
      </Grid>
    </>
  );
}
